export const READING_IMAGE_JPEG_MIME_TYPE = 'image/jpeg';
export const SUPPORTED_READING_IMAGE_ACCEPT = 'image/*,.heic,.heif';

// Geteilte AI-Bild-Konfiguration: Kamera- und Mediathek-Pfad verwenden dieselbe maximale
// Kantenlänge, damit die an die AI gesendeten Bilder konsistent groß bleiben.
export const AI_IMAGE_MAX_DIMENSION = 1280;

// JPEG-Qualität ist pro Pfad unterschiedlich: Mediathek-Fotos werden aus dem Original neu
// encodiert (0.75 reicht), Kamera-Screenshots dürfen nicht zusätzlich an Qualität verlieren
// und verwenden den höheren react-webcam-Default-Wert (0.92).
export const AI_IMAGE_JPEG_QUALITY = 0.75;
export const AI_CAMERA_SCREENSHOT_QUALITY = 0.92;

const JPEG_QUALITY = AI_IMAGE_JPEG_QUALITY;

const BLOCKED_READING_IMAGE_MIME_TYPES = new Set([
    'image/svg+xml',
    'image/tiff',
    'image/bmp',
    'image/x-icon',
    'image/vnd.microsoft.icon',
    'image/jxl',
    'application/pdf',
]);

const BLOCKED_READING_IMAGE_EXTENSIONS = new Set([
    'svg',
    'tif',
    'tiff',
    'dng',
    'cr2',
    'cr3',
    'nef',
    'arw',
    'orf',
    'rw2',
    'raf',
    'pef',
    'srw',
    'bmp',
    'ico',
    'jxl',
    'pdf',
    'mov',
    'mp4',
    'm4v',
    'avi',
    'webm',
]);

const HEIC_READING_IMAGE_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const HEIC_READING_IMAGE_EXTENSIONS = new Set(['heic', 'heif']);

export class UnsupportedReadingImageError extends Error {
    constructor(message = 'Unsupported reading image format') {
        super(message);
        this.name = 'UnsupportedReadingImageError';
    }
}

export class ReadingImageDecodeError extends Error {
    constructor(message = 'Failed to decode reading image') {
        super(message);
        this.name = 'ReadingImageDecodeError';
    }
}

const getFileExtension = (file: File): string => file.name.split('.').pop()?.toLowerCase() ?? '';

const isHeicImage = (file: File): boolean => {
    const mimeType = file.type.toLowerCase();
    const extension = getFileExtension(file);

    return HEIC_READING_IMAGE_MIME_TYPES.has(mimeType) || HEIC_READING_IMAGE_EXTENSIONS.has(extension);
};

const assertSupportedReadingImage = (file: File): void => {
    const mimeType = file.type.toLowerCase();
    const extension = getFileExtension(file);

    if (BLOCKED_READING_IMAGE_MIME_TYPES.has(mimeType) || BLOCKED_READING_IMAGE_EXTENSIONS.has(extension) || mimeType.startsWith('video/')) {
        throw new UnsupportedReadingImageError();
    }

    if (isHeicImage(file)) {
        return;
    }

    if (mimeType && !mimeType.startsWith('image/')) {
        throw new UnsupportedReadingImageError();
    }

    if (!mimeType && !extension) {
        throw new UnsupportedReadingImageError();
    }
};

const readBlobAsDataUri = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => reject(reader.error ?? new ReadingImageDecodeError('Failed to read image file'));
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
    });

const loadImage = (dataUri: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();

        image.onload = () => resolve(image);
        image.onerror = () => reject(new ReadingImageDecodeError());
        image.src = dataUri;
    });

const normalizeImageDataUriToJpeg = async (dataUri: string, maxWidth: number, maxHeight: number): Promise<string> => {
    const image = await loadImage(dataUri);
    const canvas = document.createElement('canvas');

    let width = image.width || maxWidth;
    let height = image.height || maxHeight;

    if (width > height) {
        if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
        }
    } else if (height > maxHeight) {
        width *= maxHeight / height;
        height = maxHeight;
    }

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new ReadingImageDecodeError('Failed to encode image as jpeg');
    }

    ctx.drawImage(image, 0, 0, width, height);

    const jpegDataUri = canvas.toDataURL(READING_IMAGE_JPEG_MIME_TYPE, JPEG_QUALITY);
    if (!jpegDataUri.startsWith(`data:${READING_IMAGE_JPEG_MIME_TYPE};base64,`)) {
        throw new ReadingImageDecodeError('Failed to encode image as jpeg');
    }

    return jpegDataUri;
};

const convertHeicToJpegBlob = async (file: File): Promise<Blob> => {
    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({
        blob: file,
        toType: READING_IMAGE_JPEG_MIME_TYPE,
        quality: JPEG_QUALITY,
    });

    return Array.isArray(converted) ? converted[0] : converted;
};

export const readImageFileAsDataUri = async (file: File, maxWidth = AI_IMAGE_MAX_DIMENSION, maxHeight = AI_IMAGE_MAX_DIMENSION): Promise<string> => {
    assertSupportedReadingImage(file);

    try {
        const imageBlob = isHeicImage(file) ? await convertHeicToJpegBlob(file) : file;
        const dataUri = await readBlobAsDataUri(imageBlob);
        return normalizeImageDataUriToJpeg(dataUri, maxWidth, maxHeight);
    } catch (err) {
        if (err instanceof UnsupportedReadingImageError || err instanceof ReadingImageDecodeError) {
            throw err;
        }

        throw new ReadingImageDecodeError();
    }
};
